import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { formatPrice } from "../../helpers/formattedPrice";
import walletIcon from "../../assets/images/wallet.png";
import WithdrawalModal from "./WithdrawalModal";
import WithdrawalHistoryCard from "./WithdrawalHistoryCard";
import WithdrawalInvoiceModal from "./WithdrawalInvoiceModal";
import { getWithdrawalsHistory } from "../../network";
import { toastError } from "../../helpers/toasters";
import EmptyState from "./EmptyState";

export default function Wallet({ data, onWithdrawalSuccess }) {
  const [withdrawalsHistory, setWithdrawalsHistory] = useState([]);
  const { t } = useTranslation();
  const { authorization } = useSelector((state) => state.auth);
  const [isWithdrawalOpen, setIsWithdrawalOpen] = useState(false);
  const [isInvoiceOpen, setIsInvoiceOpen] = useState(false);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const currency = authorization?.isEmirate
    ? t("buyAndSupply.aed")
    : t("buyAndSupply.egp");

  const fetchWithdrawalsHistory = useCallback(() => {
    getWithdrawalsHistory(
      (res) => {
        if (res.success) {
          setWithdrawalsHistory(res.data || []);
        } else {
          toastError(res.message);
        }
      },
      (res) => {
        toastError(res?.message);
      },
    );
  }, []);

  useEffect(() => {
    fetchWithdrawalsHistory();
  }, [fetchWithdrawalsHistory]);

  const handleWithdrawalSuccess = () => {
    fetchWithdrawalsHistory();
    onWithdrawalSuccess?.();
  };

  const handleViewInvoice = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setIsInvoiceOpen(true);
  };

  const handleCloseInvoice = () => {
    setIsInvoiceOpen(false);
    setSelectedWithdrawal(null);
  };

  return (
    <div className="buy-supply-2026-wallet-container">
      <div className="buy-supply-2026-wallet-info">
        <div className="buy-supply-2026-wallet-info-item">
          <img
            src={walletIcon}
            alt={t("buyAndSupply2026.walletBalance")}
            className="buy-supply-2026-wallet-info-item-icon"
          />
          <p className="buy-supply-2026-wallet-info-item-points">
            {t("buyAndSupply2026.walletPointsEquals", {
              points: data?.points ?? 0,
              amount: formatPrice(data?.pounds ?? 0),
              currency,
            })}
          </p>
          <p className="buy-supply-2026-wallet-info-item-balance">
            {t("buyAndSupply2026.walletBalance")}
          </p>
        </div>
        {data?.points > 0 && (
          <button
            type="button"
            className="buy-supply-2026-wallet-info-button"
            onClick={() => setIsWithdrawalOpen(true)}
          >
            {t("buyAndSupply2026.requestWithdrawals")}
          </button>
        )}
      </div>

      {withdrawalsHistory?.length > 0 && (
        <div className="buy-supply-2026-wallet-withdrawals-history">
          <h3 className="buy-supply-2026-wallet-withdrawals-history-title">
            {t("buyAndSupply2026.withdrawalsHistory")}
          </h3>
          {withdrawalsHistory?.length > 0 ? (
            <div className="buy-supply-2026-wallet-withdrawals-history-list">
              {withdrawalsHistory.map((withdrawal) => (
                <WithdrawalHistoryCard
                  key={withdrawal.id}
                  withdrawal={withdrawal}
                  currency={currency}
                  onViewInvoice={handleViewInvoice}
                />
              ))}
            </div>
          ) : (
            <p className="buy-supply-2026-wallet-withdrawals-history-empty">
              {t("buyAndSupply2026.noWithdrawalsHistory")}
            </p>
          )}
        </div>
      )}

      <WithdrawalModal
        open={isWithdrawalOpen}
        onCancel={() => setIsWithdrawalOpen(false)}
        balance={data?.pounds}
        currency={currency}
        onSuccess={handleWithdrawalSuccess}
      />

      <WithdrawalInvoiceModal
        open={isInvoiceOpen}
        onCancel={handleCloseInvoice}
        withdrawal={selectedWithdrawal}
        currency={currency}
      />
    </div>
  );
}
